import React, { useContext, useState, useEffect } from "react"
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import {
    Autorenew,
    CheckCircle,
    DirectionsCar,
    EventNote,
    Handshake,
    RadioButtonUnchecked,
} from "@mui/icons-material"
import { useMutation } from "react-query"
import { useSnackbar } from "notistack"
import AuthContext from "../../Context/auth_provider.jsx"
import useAxiosPrivate from "../../Hooks/useAxiosPrivate.js"
import { CompanyInfo } from "../SystemUsers/companyList.jsx"
import useBreakPoints from "../../Hooks/useBreakPoints.js"
import ActivityLog from "../../Component/activityLog.jsx"
import useUserPermissions from "../../Hooks/useUserPermissions.js"

function SelectionCard({ title, icon, selected, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                p: 2,
                border: "2px solid",
                borderColor: selected ? "primary.main" : "grey.300",
                borderRadius: 2,
                cursor: "pointer",
                bgcolor: selected ? "primary.50" : "background.paper",
                display: "flex",
                alignItems: "center",
                gap: 2,
                transition: "all 0.2s",
                "&:hover": {
                    borderColor: selected ? "primary.main" : "grey.400",
                    bgcolor: selected ? "primary.100" : "grey.50",
                },
            }}
        >
            <Box sx={{ color: selected ? "primary.main" : "text.secondary" }}>
                {icon}
            </Box>
            <Typography
                fontWeight="600"
                color={selected ? "primary.main" : "text.primary"}
            >
                {title}
            </Typography>
            {selected ? (
                <CheckCircle sx={{ ml: "auto", color: "primary.main" }} />
            ) : (
                <RadioButtonUnchecked
                    sx={{ ml: "auto", color: "text.disabled" }}
                />
            )}
        </Box>
    )
}

function Config() {
    const axiosPrivate = useAxiosPrivate()
    const { companyId } = useContext(AuthContext)
    // const queryClient = useQueryClient()

    const { activitiesList } = useUserPermissions()

    const { refetch, selectedCompany } = useContext(CompanyInfo)

    const {
        allow_tip: allowTip,
        max_tip: maxTip,
        min_tip: minTip,
        mid_tip: midTip,
    } = selectedCompany || {}

    // Local state for config values - updated immediately on save
    const [currentPaymentConfig, setCurrentPaymentConfig] = useState({
        automatic: false,
        manual: false,
    })
    const [currentProgramConfig, setCurrentProgramConfig] = useState({
        program: false,
        preride: false,
    })

    // Initialize and sync with selectedCompany on mount/change
    useEffect(() => {
        if (selectedCompany?.preride_payment_config) {
            setCurrentPaymentConfig(selectedCompany.preride_payment_config)
        }
        if (selectedCompany?.preride_program_config) {
            setCurrentProgramConfig(selectedCompany.preride_program_config)
        }
    }, [selectedCompany])
    console.log("slectedCompany preride configs:", {
        payment: selectedCompany?.preride_payment_config,
        program: selectedCompany?.preride_program_config,
    })
    const preridePaymentAutomatic = currentPaymentConfig.automatic || false
    const preridePaymentManual = currentPaymentConfig.manual || false
    const programEnabled = currentProgramConfig.program || false
    const prerideEnabled = currentProgramConfig.preride || false

    const { xs, md } = useBreakPoints()

    const [min, setMin] = useState(() => (minTip > 0 ? minTip : null))
    const [mid, setMid] = useState(() => (midTip > 0 ? midTip : null))
    const [max, setMax] = useState(() => (maxTip > 0 ? maxTip : null))

    // Dialog states
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
    const [programDialogOpen, setProgramDialogOpen] = useState(false)

    // Form states for dialogs
    const [paymentForm, setPaymentForm] = useState({
        automatic: false,
        manual: false,
    })
    const [programForm, setProgramForm] = useState({
        program: false,
        preride: false,
    })

    // Update displayed values when selectedCompany changes
    useEffect(() => {
        if (selectedCompany) {
            // This ensures the UI reflects the latest values after refetch
        }
    }, [selectedCompany])

    const invalidMin = min >= mid || min <= 0
    const invalidMid = min >= mid || mid >= max || mid <= 0
    const invalidMax = max > 100 || max <= mid || max <= 0

    const { enqueueSnackbar } = useSnackbar()

    // Tipping configuration mutation
    const { mutate, isLoading } = useMutation(
        (dat) => axiosPrivate.patch(`/corporate/${companyId}/tips`, dat),
        {
            onSuccess: () => {
                refetch()
                enqueueSnackbar("Updated successfully", { variant: "success" })
            },
            onError: (error) => {
                const fieldError =
                    error.response?.data?.error?.field_error?.[0]?.description
                const errorMessage = error.response?.data?.error?.message
                enqueueSnackbar(
                    fieldError || errorMessage || "Request Failed",
                    { variant: "error" }
                )
            },
        }
    )

    // Preride payment type configuration mutation
    const { mutate: mutatePaymentType, isLoading: isPaymentTypeLoading } =
        useMutation(
            (dat) =>
                axiosPrivate.patch(
                    `/corporate/${companyId}/preride/payment-type`,
                    dat
                ),
            {
                onSuccess: async (response, variables) => {
                    // Immediately update local state for instant UI feedback
                    setCurrentPaymentConfig(variables)
                    // Close dialog and show success
                    setPaymentDialogOpen(false)
                    enqueueSnackbar("Payment type updated successfully", {
                        variant: "success",
                    })
                    // Background refetch to keep everything in sync

                    refetch()
                },
                onError: (error) => {
                    const fieldError =
                        error.response?.data?.error?.field_error?.[0]
                            ?.description
                    const errorMessage = error.response?.data?.error?.message
                    enqueueSnackbar(
                        fieldError || errorMessage || "Request Failed",
                        { variant: "error" }
                    )
                },
            }
        )

    // Program/Preride configuration mutation
    const { mutate: mutateProgramPreride, isLoading: isProgramPrerideLoading } =
        useMutation(
            (dat) =>
                axiosPrivate.patch(
                    `/corporate/${companyId}/preride-program-config`,
                    dat
                ),
            {
                onSuccess: async (response, variables) => {
                    // Immediately update local state for instant UI feedback
                    setCurrentProgramConfig(variables)
                    // Close dialog and show success
                    setProgramDialogOpen(false)
                    enqueueSnackbar("Configuration updated successfully", {
                        variant: "success",
                    })
                    // Background refetch to keep everything in sync
                    refetch()
                },
                onError: (error) => {
                    const fieldError =
                        error.response?.data?.error?.field_error?.[0]
                            ?.description
                    const errorMessage = error.response?.data?.error?.message
                    enqueueSnackbar(
                        fieldError || errorMessage || "Request Failed",
                        { variant: "error" }
                    )
                },
            }
        )

    const handleCheckbox = (e) => {
        const { checked } = e.currentTarget
        mutate(
            {
                allow_tip: checked ? "ACTIVE" : "INACTIVE",
            },
            {
                onSuccess: () => {
                    refetch()
                },
                onError: (error) => {
                    const fieldError =
                        error.response?.data?.error?.field_error?.[0]
                            ?.description
                    const errorMessage = error.response?.data?.error?.message
                    enqueueSnackbar(
                        fieldError || errorMessage || "Request Failed",
                        { variant: "error" }
                    )
                },
            }
        )
    }

    // Dialog handlers
    const handleOpenPaymentDialog = () => {
        setPaymentForm({
            automatic: preridePaymentAutomatic,
            manual: preridePaymentManual,
        })
        setPaymentDialogOpen(true)
    }

    const handleOpenProgramDialog = () => {
        setProgramForm({
            program: programEnabled,
            preride: prerideEnabled,
        })
        setProgramDialogOpen(true)
    }

    const handleSavePaymentType = () => {
        mutatePaymentType(paymentForm)
    }

    const handleSaveProgramPreride = () => {
        mutateProgramPreride(programForm)
    }

    return (
        <Box
            sx={{
                ml: xs && !md ? "1em" : "4em",
                mr: "1em",
                mt: "3em",
                pb: "2em",
                display: "flex",
                flexDirection: { md: "row", xs: "column" },
                gap: "2em",
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2em" }}>
                {/* Tipping Configuration */}
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Tipping Configuration
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <FormControlLabel
                        disabled={isLoading}
                        checked={allowTip === "ACTIVE"}
                        onChange={handleCheckbox}
                        label="Enable Tipping"
                        control={<Checkbox />}
                    />
                    {allowTip === "ACTIVE" && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "1em",
                                mt: "1.7em",
                                ml: xs && !md ? undefined : "2em",
                            }}
                        >
                            <Grid
                                container
                                direction={xs && !md ? "column" : undefined}
                            >
                                <Grid item xs={3}>
                                    <Typography>Low</Typography>{" "}
                                </Grid>
                                <Grid item xs={9}>
                                    <TextField
                                        fullWidth
                                        value={min || ""}
                                        label={invalidMin && "invalid value"}
                                        error={invalidMin}
                                        type="number"
                                        onChange={(e) =>
                                            setMin(Number(e.target.value))
                                        }
                                        InputProps={{
                                            inputProps: {
                                                max: 100,
                                                sx: {
                                                    backgroundColor: "white",
                                                    mr: ".5em",
                                                },
                                            },
                                            sx: {
                                                "&.MuiInputBase-root": {
                                                    pr: "0.5em",
                                                    backgroundColor: "#dfdfdf",
                                                },
                                            },
                                            endAdornment: (
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    %
                                                </Typography>
                                            ),
                                        }}
                                        helperText={
                                            <Box>
                                                <Typography
                                                    varaint="subtitle2"
                                                    sx={{
                                                        fontWeight: "light",
                                                        fontSize: "1em",
                                                    }}
                                                >
                                                    Should be less than medium
                                                    value and greater than zero
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction={xs && !md ? "column" : undefined}
                            >
                                <Grid item xs={3}>
                                    <Typography>Medium</Typography>{" "}
                                </Grid>
                                <Grid item xs={9}>
                                    <TextField
                                        fullWidth
                                        value={mid || ""}
                                        label={invalidMid && "invalid value"}
                                        error={invalidMid}
                                        onChange={(e) =>
                                            setMid(Number(e.target.value))
                                        }
                                        type="number"
                                        InputProps={{
                                            inputProps: {
                                                max: 100,
                                                sx: {
                                                    backgroundColor: "white",
                                                    mr: ".5em",
                                                },
                                            },
                                            sx: {
                                                "&.MuiInputBase-root": {
                                                    pr: "0.5em",
                                                    backgroundColor: "#dfdfdf",
                                                },
                                            },
                                            endAdornment: (
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    %
                                                </Typography>
                                            ),
                                        }}
                                        helperText={
                                            <Box>
                                                <Typography
                                                    varaint="subtitle2"
                                                    sx={{
                                                        fontWeight: "light",
                                                        fontSize: "1em",
                                                    }}
                                                >
                                                    Should be less than Maximum
                                                    and greater than minimum
                                                    value
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction={xs && !md ? "column" : undefined}
                            >
                                <Grid item xs={3}>
                                    <Typography>High</Typography>{" "}
                                </Grid>
                                <Grid item xs={9}>
                                    <TextField
                                        fullWidth
                                        value={max || ""}
                                        type="number"
                                        label={invalidMax && "invalid value"}
                                        error={invalidMax}
                                        onChange={(e) =>
                                            setMax(Number(e.target.value))
                                        }
                                        InputProps={{
                                            inputProps: {
                                                max: 100,
                                                sx: {
                                                    backgroundColor: "white",
                                                    mr: ".5em",
                                                },
                                            },
                                            sx: {
                                                "&.MuiInputBase-root": {
                                                    pr: "0.5em",
                                                    backgroundColor: "#dfdfdf",
                                                },
                                            },
                                            endAdornment: (
                                                <Typography
                                                    sx={{
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    %
                                                </Typography>
                                            ),
                                        }}
                                        helperText={
                                            <Box>
                                                <Typography
                                                    varaint="subtitle2"
                                                    sx={{
                                                        fontWeight: "light",
                                                        fontSize: "1em",
                                                    }}
                                                >
                                                    Should be less than 100 and
                                                    greater than medium value
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Box>
                                <Button
                                    disabled={
                                        invalidMin || invalidMax || invalidMid
                                    }
                                    onClick={() =>
                                        mutate({
                                            max_tip: max,
                                            min_tip: min,
                                            mid_tip: mid,
                                            allow_tip: "ACTIVE",
                                        })
                                    }
                                    startIcon={
                                        isLoading ? (
                                            <CircularProgress
                                                size="1em"
                                                sx={{ color: "white" }}
                                            />
                                        ) : null
                                    }
                                    variant="contained"
                                    sx={{
                                        mt: "1em",
                                    }}
                                >
                                    Save
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Program/Preride Configuration */}
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        "&:hover": {
                            boxShadow: 2,
                        },
                    }}
                >
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6" fontWeight="600">
                                Program & Preride Configuration
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleOpenProgramDialog}
                                color="primary"
                                sx={{
                                    bgcolor: "primary.50",
                                    "&:hover": {
                                        bgcolor: "primary.100",
                                    },
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        p: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                    }}
                                >
                                    <EventNote
                                        color={
                                            programEnabled
                                                ? "primary"
                                                : "disabled"
                                        }
                                    />
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight="600"
                                        >
                                            Program
                                        </Typography>
                                        <Chip
                                            label={
                                                programEnabled
                                                    ? "Enabled"
                                                    : "Disabled"
                                            }
                                            size="small"
                                            color={
                                                programEnabled
                                                    ? "success"
                                                    : "default"
                                            }
                                            variant={
                                                programEnabled
                                                    ? "filled"
                                                    : "outlined"
                                            }
                                            sx={{ height: 20, fontSize: 10 }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2,
                                        p: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                    }}
                                >
                                    <DirectionsCar
                                        color={
                                            prerideEnabled
                                                ? "primary"
                                                : "disabled"
                                        }
                                    />
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight="600"
                                        >
                                            Preride
                                        </Typography>
                                        <Chip
                                            label={
                                                prerideEnabled
                                                    ? "Enabled"
                                                    : "Disabled"
                                            }
                                            size="small"
                                            color={
                                                prerideEnabled
                                                    ? "success"
                                                    : "default"
                                            }
                                            variant={
                                                prerideEnabled
                                                    ? "filled"
                                                    : "outlined"
                                            }
                                            sx={{ height: 20, fontSize: 10 }}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Preride Payment Type Configuration */}
                
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            "&:hover": {
                                boxShadow: 2,
                            },
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="h6" fontWeight="600">
                                    Preride Payment Type
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={handleOpenPaymentDialog}
                                    color="primary"
                                    sx={{
                                        bgcolor: "primary.50",
                                        "&:hover": {
                                            bgcolor: "primary.100",
                                        },
                                    }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            p: 1.5,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Autorenew
                                            color={
                                                preridePaymentAutomatic
                                                    ? "primary"
                                                    : "disabled"
                                            }
                                        />
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="600"
                                            >
                                                Automatic Payment
                                            </Typography>
                                            <Chip
                                                label={
                                                    preridePaymentAutomatic
                                                        ? "Enabled"
                                                        : "Disabled"
                                                }
                                                size="small"
                                                color={
                                                    preridePaymentAutomatic
                                                        ? "success"
                                                        : "default"
                                                }
                                                variant={
                                                    preridePaymentAutomatic
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                sx={{
                                                    height: 20,
                                                    fontSize: 10,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            p: 1.5,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Handshake
                                            color={
                                                preridePaymentManual
                                                    ? "primary"
                                                    : "disabled"
                                            }
                                        />
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight="600"
                                            >
                                                Manual Payment
                                            </Typography>
                                            <Chip
                                                label={
                                                    preridePaymentManual
                                                        ? "Enabled"
                                                        : "Disabled"
                                                }
                                                size="small"
                                                color={
                                                    preridePaymentManual
                                                        ? "success"
                                                        : "default"
                                                }
                                                variant={
                                                    preridePaymentManual
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                sx={{
                                                    height: 20,
                                                    fontSize: 10,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                

                {/* Payment Type Dialog */}
                <Dialog
                    open={paymentDialogOpen}
                    onClose={() => setPaymentDialogOpen(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle sx={{ fontWeight: "bold" }}>
                        Edit Preride Payment Type
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Tap on the cards below to enable or disable payment
                            methods.
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                pt: 1,
                            }}
                        >
                            <SelectionCard
                                title="Automatic Payment"
                                icon={<Autorenew fontSize="large" />}
                                selected={paymentForm.automatic}
                                onClick={() =>
                                    setPaymentForm({
                                        ...paymentForm,
                                        automatic: !paymentForm.automatic,
                                    })
                                }
                            />
                            <SelectionCard
                                title="Manual Payment"
                                icon={<Handshake fontSize="large" />}
                                selected={paymentForm.manual}
                                onClick={() =>
                                    setPaymentForm({
                                        ...paymentForm,
                                        manual: !paymentForm.manual,
                                    })
                                }
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button
                            onClick={() => setPaymentDialogOpen(false)}
                            sx={{ color: "text.secondary" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSavePaymentType}
                            variant="contained"
                            disabled={isPaymentTypeLoading}
                            startIcon={
                                isPaymentTypeLoading ? (
                                    <CircularProgress size={16} />
                                ) : null
                            }
                            sx={{ px: 3 }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Program/Preride Dialog */}
                <Dialog
                    open={programDialogOpen}
                    onClose={() => setProgramDialogOpen(false)}
                    maxWidth="xs"
                    fullWidth
                >
                    <DialogTitle sx={{ fontWeight: "bold" }}>
                        Edit Program & Preride Configuration
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 3 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            Tap on the cards below to enable or disable
                            features.
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                pt: 1,
                            }}
                        >
                            <SelectionCard
                                title="Enable Program"
                                icon={<EventNote fontSize="large" />}
                                selected={programForm.program}
                                onClick={() =>
                                    setProgramForm({
                                        ...programForm,
                                        program: !programForm.program,
                                    })
                                }
                            />
                            <SelectionCard
                                title="Enable Preride"
                                icon={<DirectionsCar fontSize="large" />}
                                selected={programForm.preride}
                                onClick={() =>
                                    setProgramForm({
                                        ...programForm,
                                        preride: !programForm.preride,
                                    })
                                }
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button
                            onClick={() => setProgramDialogOpen(false)}
                            sx={{ color: "text.secondary" }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveProgramPreride}
                            variant="contained"
                            disabled={isProgramPrerideLoading}
                            startIcon={
                                isProgramPrerideLoading ? (
                                    <CircularProgress size={16} />
                                ) : null
                            }
                            sx={{ px: 3 }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
            {activitiesList && (
                <Box sx={{ flex: 1 }}>
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: "bold", pb: "0px", pl: "16px" }}
                    >
                        Activity Log
                    </Typography>
                    <Divider variant="middle" sx={{ pb: "1em" }} />
                    <ActivityLog
                        url="activities"
                        filter={[
                            {
                                column_field: "url",
                                operator_value: "contains",
                                value: `v2/corporate/${companyId}/tips`,
                            },
                        ]}
                    />
                </Box>
            )}
        </Box>
    )
}

export default Config
